import { useState } from "react";
import { FaTimes, FaBars } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import mslLogo from "../assets/msllogo.png";
import SecurityAlert from "./SecurityAlert";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "EMI Calculator", path: "/loan-calculator" },
  { label: "Key Differentiators", path: "/why-choose-us" },
  { label: "How it works", path: "/how-it-works" },
  { label: "About Us", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSecurityAlert, setShowSecurityAlert] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (e: React.MouseEvent<HTMLAnchorElement>, path: string) => {
    e.preventDefault();
    setMenuOpen(false);
    
    if (path.startsWith('/')) {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(path.replace('#', ''));
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        const element = document.getElementById(path.replace('#', ''));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }
  };

  const handleSecurityConfirm = () => {
    setShowSecurityAlert(false);
    navigate('/repay');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <div>
      <SecurityAlert 
        isOpen={showSecurityAlert}
        onClose={() => setShowSecurityAlert(false)}
        onConfirm={handleSecurityConfirm}
      />
      <nav className="w-full bg-white/95 backdrop-blur border-b border-neutral-200 shadow-sm fixed top-0 left-0 z-50 font-['Inter']">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 px-4 md:px-6 lg:px-8 py-3">
          <a href="/" onClick={(e) => handleNavigation(e, '/')} className="flex items-center shrink-0">
            <img
              className="h-8 sm:h-10 object-contain"
              src={mslLogo}
              alt="MySalaryLoan"
            />
          </a>

          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {NAV_LINKS.map(({ label, path }) => (
              <a
                key={path}
                href={path}
                onClick={(e) => handleNavigation(e, path)}
                className={`relative whitespace-nowrap text-sm xl:text-[15px] font-medium transition-colors hover:text-[#21A6B5] after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:rounded-full after:bg-[#21A6B5] after:transition-all ${
                  isActive(path) ? "text-[#21A6B5] after:w-full" : "text-[#2C3E50] after:w-0 hover:after:w-full"
                }`}
              >
                {label}
              </a>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
            <a
              href={import.meta.env.VITE_WEBSITE_LINK}
              target="_blank"
              className="whitespace-nowrap px-4 xl:px-5 py-2 rounded-full bg-[#3D4A7C] text-white text-sm font-semibold hover:bg-[#2d3a6c] transition-colors shadow-sm"
            >
              Get Funds Now
            </a>
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`}
              className="whitespace-nowrap px-4 xl:px-5 py-2 rounded-full bg-[#21A6B5] text-white text-sm font-semibold hover:bg-[#1B8E9B] transition-colors shadow-sm"
            >
              Repay Now
            </a>
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/login`}
              target="_blank"
              className="whitespace-nowrap px-4 xl:px-5 py-1.5 rounded-full border-2 border-[#3D4A7C] text-[#3D4A7C] text-sm font-semibold hover:bg-[#3D4A7C] hover:text-white transition-colors"
            >
              Login
            </a>
          </div>

          <button
            className="lg:hidden text-[#3D4A7C] text-2xl"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {menuOpen && (
          <div className="lg:hidden flex flex-col items-center gap-2 pt-2 pb-6 bg-white shadow-lg border-t border-neutral-200">
            {NAV_LINKS.map(({ label, path }) => (
              <a
                key={path}
                href={path}
                onClick={(e) => handleNavigation(e, path)}
                className={`w-[90%] text-center px-5 py-2 rounded-md font-medium transition-colors hover:text-[#21A6B5] hover:bg-[#EFFFFD] ${
                  isActive(path) ? "text-[#21A6B5] bg-[#EFFFFD]" : "text-[#2C3E50]"
                }`}
              >
                {label}
              </a>
            ))}

            <a
              href={import.meta.env.VITE_WEBSITE_LINK}
              target="_blank"
              className="w-[90%] mt-2 text-center px-5 py-2.5 rounded-full bg-[#3D4A7C] text-white font-semibold hover:bg-[#2d3a6c] transition-colors"
            >
              Get Funds Now
            </a>
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/repay-now`}
              className="w-[90%] text-center px-5 py-2.5 rounded-full bg-[#21A6B5] text-white font-semibold hover:bg-[#1B8E9B] transition-colors"
            >
              Repay Now
            </a>
            <a
              href={`${import.meta.env.VITE_WEBSITE_LINK}/login`}
              target="_blank"
              className="w-[90%] text-center px-5 py-2 rounded-full border-2 border-[#3D4A7C] text-[#3D4A7C] text-sm font-semibold hover:bg-[#3D4A7C] hover:text-white transition-colors"
            >
              Login
            </a>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Navbar;
